(function () {
  'use strict';

  const collapse = (el, duration = 250) => {
    if (!el || el.hidden) return;
    el.style.height = el.scrollHeight + 'px';
    el.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transition = `height ${duration}ms ease`;
        el.style.height = '0';
        el.addEventListener('transitionend', () => {
          el.hidden = true;
          el.style.removeProperty('height');
          el.style.removeProperty('overflow');
          el.style.removeProperty('transition');
        }, { once: true });
      });
    });
  };

  const expand = (el, duration = 250) => {
    if (!el) return;
    el.hidden = false;
    const height = el.scrollHeight;
    el.style.height = '0';
    el.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transition = `height ${duration}ms ease`;
        el.style.height = height + 'px';
        el.addEventListener('transitionend', () => {
          el.style.removeProperty('height');
          el.style.removeProperty('overflow');
          el.style.removeProperty('transition');
        }, { once: true });
      });
    });
  };

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightEl = (el, query, originalHTML) => {
    if (!query) {
      el.innerHTML = originalHTML;
      return;
    }
    const regex = new RegExp(`(${escapeRegex(query)})(?![^<]*>)`, 'gi');
    el.innerHTML = originalHTML.replace(regex, '<mark class="knowledge-hub__highlight">$1</mark>');
  };

  // Initialise a single section — returns a filter function that accepts a global search query
  const initSection = (sectionEl) => {
    const tabs = sectionEl.querySelectorAll('.knowledge-hub__tab');
    const faqItems = sectionEl.querySelectorAll('.knowledge-hub__faq-item');
    const noResults = sectionEl.querySelector('.knowledge-hub__no-results');

    const firstTab = sectionEl.querySelector('.knowledge-hub__tab:not(.knowledge-hub__tab-toggle)');
    let activeCategory = firstTab ? firstTab.dataset.category : null;
    const toggleBtn = sectionEl.querySelector('.knowledge-hub__tab-toggle');
    const searchExpanded = new Set();

    const originals = new Map();
    faqItems.forEach((item) => {
      const q = item.querySelector('.knowledge-hub__faq-question');
      const a = item.querySelector('.knowledge-hub__faq-answer');
      originals.set(item, {
        q: q ? q.innerHTML : '',
        a: a ? a.innerHTML : ''
      });
    });

    const filterFaqs = (searchQuery) => {
      let visible = 0;
      faqItems.forEach((item) => {
        const matchCat = !activeCategory || item.dataset.category === activeCategory;
        const matchAnswerOnly = searchQuery && !item.dataset.question.includes(searchQuery) && item.dataset.answer.includes(searchQuery);
        const matchSearch = !searchQuery ||
          item.dataset.question.includes(searchQuery) ||
          item.dataset.answer.includes(searchQuery);
        const show = matchCat && matchSearch;
        item.classList.toggle('is-hidden', !show);
        if (show) visible++;

        const orig = originals.get(item);
        const q = item.querySelector('.knowledge-hub__faq-question');
        const a = item.querySelector('.knowledge-hub__faq-answer');
        if (q) highlightEl(q, searchQuery, orig.q);
        if (a) highlightEl(a, searchQuery, orig.a);

        const answer = item.querySelector('.knowledge-hub__faq-answer');
        const toggle = item.querySelector('.knowledge-hub__faq-toggle');
        if (show && matchAnswerOnly && !item.classList.contains('is-open')) {
          searchExpanded.add(item);
          item.classList.add('is-open');
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
          expand(answer);
        } else if (searchExpanded.has(item) && (!searchQuery || !matchAnswerOnly)) {
          searchExpanded.delete(item);
          item.classList.remove('is-open');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          collapse(answer);
        }
      });
      if (noResults) noResults.classList.toggle('is-visible', visible === 0);
    };

    const categoryTabs = [...tabs].filter((t) => !t.classList.contains('knowledge-hub__tab-toggle'));

    const updateToggleLabel = () => {
      if (toggleBtn) {
        toggleBtn.textContent = activeCategory ? 'Select all' : 'Clear';
      }
    };

    const selectAll = () => {
      activeCategory = null;
      categoryTabs.forEach((t) => {
        t.classList.add('knowledge-hub__tab--active');
        t.setAttribute('aria-selected', 'true');
      });
      updateToggleLabel();
    };

    const selectCategory = (tab) => {
      categoryTabs.forEach((t) => {
        t.classList.remove('knowledge-hub__tab--active');
        t.setAttribute('aria-selected', 'false');
      });
      activeCategory = tab.dataset.category;
      tab.classList.add('knowledge-hub__tab--active');
      tab.setAttribute('aria-selected', 'true');
      updateToggleLabel();
    };

    categoryTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.classList.contains('knowledge-hub__tab--active') && !activeCategory) {
          selectCategory(tab);
        } else if (tab.classList.contains('knowledge-hub__tab--active')) {
          selectAll();
        } else {
          selectCategory(tab);
        }
        filterFaqs(sectionEl._getSearchQuery ? sectionEl._getSearchQuery() : '');
      });
    });

    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        if (activeCategory) {
          selectAll();
        } else {
          const first = categoryTabs[0];
          if (first) selectCategory(first);
        }
        filterFaqs(sectionEl._getSearchQuery ? sectionEl._getSearchQuery() : '');
      });
    }

    updateToggleLabel();

    faqItems.forEach((item) => {
      const faqToggle = item.querySelector('.knowledge-hub__faq-toggle');
      const answer = item.querySelector('.knowledge-hub__faq-answer');
      if (!faqToggle || !answer) return;

      faqToggle.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        faqItems.forEach((other) => {
          if (other !== item && other.classList.contains('is-open')) {
            other.classList.remove('is-open');
            other.querySelector('.knowledge-hub__faq-toggle').setAttribute('aria-expanded', 'false');
            collapse(other.querySelector('.knowledge-hub__faq-answer'));
            searchExpanded.delete(other);
          }
        });
        if (isOpen) {
          item.classList.remove('is-open');
          faqToggle.setAttribute('aria-expanded', 'false');
          collapse(answer);
          searchExpanded.delete(item);
        } else {
          item.classList.add('is-open');
          faqToggle.setAttribute('aria-expanded', 'true');
          expand(answer);
        }
      });
    });

    return filterFaqs;
  };

  // Sidebar navigation: smooth scroll + scroll spy
  const initNav = (hub) => {
    const links = hub.querySelectorAll('[data-kh-nav]');
    const panels = hub.querySelectorAll('[data-kh-panel]');

    if (!links.length || !panels.length) return;

    var isScrolling = false;

    // Click: smooth scroll to panel
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var handle = this.getAttribute('data-kh-nav');
        var target = hub.querySelector('[data-kh-panel="' + handle + '"]');
        if (!target) return;

        isScrolling = true;
        links.forEach(function (l) { l.classList.remove('knowledge-hub__nav-link--active'); });
        this.classList.add('knowledge-hub__nav-link--active');

        var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
        var top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 32;
        window.scrollTo({ top: top, behavior: 'smooth' });

        setTimeout(function () { isScrolling = false; }, 800);
      });
    });

    // Scroll spy: highlight active nav link based on scroll position
    var observer = new IntersectionObserver(function (entries) {
      if (isScrolling) return;
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var handle = entry.target.getAttribute('data-kh-panel');
          links.forEach(function (l) {
            l.classList.toggle('knowledge-hub__nav-link--active', l.getAttribute('data-kh-nav') === handle);
          });
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });

    panels.forEach(function (panel) {
      observer.observe(panel);
    });
  };

  const initKnowledgeHub = (hub) => {
    if (!hub) return;

    let globalSearchQuery = '';

    const sectionFilters = [];
    hub.querySelectorAll('.knowledge-hub__section').forEach((sectionEl) => {
      sectionEl._getSearchQuery = () => globalSearchQuery;
      sectionFilters.push(initSection(sectionEl));
    });

    const runAllFilters = () => {
      sectionFilters.forEach((filter) => filter(globalSearchQuery));
    };

    const globalSearch = hub.querySelector('.knowledge-hub__search-input--global');
    if (globalSearch) {
      globalSearch.addEventListener('input', () => {
        globalSearchQuery = globalSearch.value.toLowerCase().trim();
        runAllFilters();
      });
    }

    runAllFilters();

    // Init sidebar navigation
    initNav(hub);
  };

  const init = () => {
    document.querySelectorAll('.knowledge-hub-section').forEach((el) => {
      initKnowledgeHub(el.querySelector('.knowledge-hub'));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initKnowledgeHub(event.target.querySelector('.knowledge-hub'));
  });
})();
